import { Helmet } from "react-helmet-async";

/**
 * FreelanZen SEO head — configurable per page.
 *
 * Renders <title>, meta description, canonical, Open Graph and Twitter Card
 * tags, plus optional JSON-LD structured data. Pass only what you want to
 * override; the rest falls back to sensible site-wide defaults.
 *
 * Usage:
 *   <Seo
 *     title="FreelanZen — Il lavoro da freelance, senza il caos"
 *     description="CRM, WhatsApp, fatturazione elettronica e solleciti AI..."
 *     path="/"
 *     image="/brand/og-image.png"
 *     jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", ... }}
 *   />
 */
const SITE_NAME = "FreelanZen";
const DEFAULT_TITLE = "FreelanZen — Il lavoro da freelance, in un unico spazio calmo";
const DEFAULT_DESCRIPTION =
  "CRM, inbox WhatsApp, fatturazione elettronica e solleciti AI per freelance italiani: unifica clienti, progetti e fatture in un unico spazio calmo.";
const DEFAULT_IMAGE = "/brand/og-image.png";
const DEFAULT_TWITTER_HANDLE = "@freelanzen";

function siteOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "";
}

export default function Seo({
  title,
  description,
  path,
  image,
  imageAlt,
  type = "website",
  locale = "it_IT",
  noIndex = false,
  jsonLd,
  children,
}) {
  const origin = siteOrigin();
  const finalTitle = title || DEFAULT_TITLE;
  const finalDescription = description || DEFAULT_DESCRIPTION;
  const canonical = path ? `${origin}${path}` : (typeof window !== "undefined" ? window.location.href.split("?")[0] : origin);
  const imgPath = image || DEFAULT_IMAGE;
  const finalImage = imgPath.startsWith("http") ? imgPath : `${origin}${imgPath}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={finalImage} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:locale" content={locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={DEFAULT_TWITTER_HANDLE} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      {children}
    </Helmet>
  );
}
